"use client";

import { useState, useEffect } from "react";
import { useTranslations } from "next-intl";
import { useSession } from "@/lib/auth-react";
import { useRouter } from "next/navigation";
import { useLogout } from "@/hooks/useLogout";
import { startImpersonation } from "@/utils/impersonate";
import { Shield, ChevronDown, ArrowLeft, Users } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export interface RecentAccount {
  id: string;
  name: string;
  role: string;
  image: string | null;
}

const STORAGE_KEY = "rawiah_recent_impersonations";
const MAX_RECENT = 3;

export function getRecentAccounts(): RecentAccount[] {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
    // Guard against a corrupted (non-array) value — callers do .filter().
    return Array.isArray(parsed) ? (parsed as RecentAccount[]) : [];
  } catch {
    return [];
  }
}

/** Overwrite the recents store — used to prune accounts that no longer exist. */
export function setRecentAccounts(accounts: RecentAccount[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(accounts.slice(0, MAX_RECENT)));
  } catch {
    /* best-effort */
  }
}

export function trackImpersonation(user: { id: string; name: string; role: string; image: string | null }) {
  const recent = getRecentAccounts().filter((a) => a.id !== user.id);
  recent.unshift({ id: user.id, name: user.name, role: user.role, image: user.image });
  localStorage.setItem(STORAGE_KEY, JSON.stringify(recent.slice(0, MAX_RECENT)));
}

export function ImpersonationPill() {
  const t = useTranslations("ImpersonationBanner");
  const { data: session } = useSession();
  const { handleLogout } = useLogout();
  const [recentAccounts, setRecentAccounts] = useState<RecentAccount[]>([]);
  const [switching, setSwitching] = useState<string | null>(null);

  useEffect(() => {
    if (session?.impersonatedBy) {
      setRecentAccounts(getRecentAccounts().filter((a) => a.id !== session.user?.id));
    }
  }, [session?.impersonatedBy, session?.user?.id]);

  if (!session?.impersonatedBy) return null;

  const handleSwitch = async (account: RecentAccount) => {
    setSwitching(account.id);
    try {
      // Restore the admin session first (so the next request is
      // authorised as admin), then start a fresh impersonation against
      // the new target. startImpersonation hard-reloads on success.
      await fetch("/api/admin/impersonate/stop", {
        method: "POST",
        credentials: "include",
      });
      await startImpersonation(account.id);
    } catch (err) {
      console.error("Switch impersonation failed:", err);
      setSwitching(null);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid="impersonationPill"
          // Compact: shield + chevron only. The amber colour already signals
          // impersonation and the dropdown header says who you're acting as,
          // so the "Impersonating" label was just navbar clutter on mobile.
          aria-label={t("openMenuAriaLabel")}
          title={t("impersonatingTitle")}
          className="flex cursor-pointer items-center gap-1 rounded-full bg-amber-500 px-2.5 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-amber-600"
        >
          <Shield className="h-3.5 w-3.5" />
          <ChevronDown className="h-3 w-3 transition-transform group-data-[state=open]:rotate-180" />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        className="w-[min(14rem,calc(100vw-2rem))] max-h-[calc(100vh-6rem)] overflow-y-auto rounded-xl border border-amber-200 bg-white p-0 shadow-xl"
      >
        <DropdownMenuLabel className="border-b border-amber-100 bg-amber-50 px-4 py-2.5 font-normal">
          <p className="text-xs font-medium text-amber-800">
            {t.rich("actingAs", {
              name: session.user?.name ?? "",
              strong: (chunks) => <strong>{chunks}</strong>,
            })}
          </p>
          <p className="text-[10px] text-amber-600">{session.user?.role}</p>
        </DropdownMenuLabel>

        {recentAccounts.length > 0 && (
          <>
            <div className="px-3 py-2">
              <p className="mb-1.5 flex items-center gap-1 text-[10px] font-medium uppercase tracking-wide text-gray-400">
                <Users className="h-3 w-3" />
                {t("recent")}
              </p>
              {recentAccounts.map((account) => (
                <DropdownMenuItem
                  key={account.id}
                  disabled={switching !== null}
                  onSelect={(e) => {
                    e.preventDefault();
                    handleSwitch(account);
                  }}
                  className="flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-xs text-gray-700 focus:bg-gray-100 disabled:opacity-50"
                >
                  <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#27335D] text-[10px] font-bold text-white">
                    {account.name?.charAt(0) ?? "?"}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium">{account.name}</p>
                    <p className="text-[10px] text-gray-400">{account.role}</p>
                  </div>
                  {switching === account.id && (
                    <div className="h-3 w-3 animate-spin rounded-full border border-gray-400 border-t-transparent" />
                  )}
                </DropdownMenuItem>
              ))}
            </div>
            <DropdownMenuSeparator className="bg-gray-100" />
          </>
        )}

        <div className="p-2">
          <DropdownMenuItem
            onSelect={() => handleLogout("/dashboard/users")}
            data-testid="stopImpersonationButton"
            className="flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-2 text-xs font-medium text-amber-700 focus:bg-amber-50"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            {t("backToAdmin")}
          </DropdownMenuItem>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
